import React from "react";
import { Modal } from "rsuite";
import { Equipament, statusLabels, EquipamentStatus } from "../../../../interfaces/Equipament";
import { lastSeenLabel } from "../widgetVersion";
import FleetAgentsPanel from "./FleetAgentsPanel";
import FleetSessionsPanel from "./FleetSessionsPanel";

interface ModalComputerDetailProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  computer: Equipament | null;
  /** Recarrega a lista de trás quando algo muda aqui (posse, credencial revogada). */
  onChanged?: () => void;
}

const labelCls = "text-sm font-semibold text-gray-700 dark:text-gray-300";
const valueCls =
  "mt-1.5 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5 text-sm text-navy-700 dark:border-white/10 dark:bg-navy-900 dark:text-white";

const statusBadge: Record<EquipamentStatus, string> = {
  em_uso: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  disponivel: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  danificado: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

const ModalComputerDetail: React.FC<ModalComputerDetailProps> = ({
  open,
  setOpen,
  computer,
  onChanged,
}) => {
  const handleClose = () => {
    setOpen(false);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("pt-BR");
  };

  if (!computer) return null;

  return (
    <Modal open={open} size="md" onClose={handleClose}>
      <Modal.Header>
        <Modal.Title>
          <b className="text-lg font-extrabold text-navy-700 dark:text-white">Detalhes do Equipamento</b>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Serial Number */}
          <div className="flex flex-col">
            <span className={labelCls}>Número de Série</span>
            <p className={valueCls}>{computer.serieNumber}</p>
          </div>

          {/* Model */}
          <div className="flex flex-col">
            <span className={labelCls}>Modelo</span>
            <p className={valueCls}>{computer.model}</p>
          </div>

          {/* Year */}
          <div className="flex flex-col">
            <span className={labelCls}>Ano</span>
            <p className={`${valueCls} tabular-nums`}>{computer.year}</p>
          </div>

          {/* Processor */}
          <div className="flex flex-col">
            <span className={labelCls}>Processador</span>
            <p className={valueCls}>{computer.processor}</p>
          </div>

          {/* Memory */}
          <div className="flex flex-col">
            <span className={labelCls}>Memória</span>
            <p className={valueCls}>{computer.memory}</p>
          </div>

          {/* Storage */}
          <div className="flex flex-col">
            <span className={labelCls}>HD/Armazenamento</span>
            <p className={valueCls}>{computer.storage}</p>
          </div>

          {/* Status */}
          <div className="flex flex-col">
            <span className={labelCls}>Status</span>
            <div className="mt-1.5">
              <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusBadge[computer.status]}`}>
                {statusLabels[computer.status]}
              </span>
            </div>
          </div>

          {/* Purchase Date */}
          <div className="flex flex-col">
            <span className={labelCls}>Data de Compra</span>
            <p className={`${valueCls} tabular-nums`}>{formatDate(computer.purchaseDate)}</p>
          </div>

          {/* Current User */}
          <div className="flex flex-col sm:col-span-2">
            <span className={labelCls}>Usuário Atual</span>
            <p className={valueCls}>{computer.actualUser || "Nenhum usuário atribuído"}</p>
          </div>

          {/* Current Start Date */}
          {computer.actualUser && (
            <div className="flex flex-col sm:col-span-2">
              <span className={labelCls}>Data de Início (Uso Atual)</span>
              <p className={`${valueCls} tabular-nums`}>{formatDate(computer.initialDate)}</p>
            </div>
          )}

          {/* Widget — reportado pela própria máquina, não digitado por ninguém */}
          <div className="flex flex-col">
            <span className={labelCls}>Versão do widget</span>
            <p className={`${valueCls} tabular-nums`}>
              {computer.widgetVersion || "Ainda não reportou"}
            </p>
          </div>

          {/* O porquê, cru: é isto que alguém leva para um chamado, em vez de "não atualiza". */}
          {computer.widgetUpdateError && (
            <div className="flex flex-col sm:col-span-2">
              <span className={labelCls}>Erro do updater</span>
              <p className="mt-1.5 break-words rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 font-mono text-xs text-red-700 dark:border-red-500/30 dark:bg-red-900/20 dark:text-red-300">
                {computer.widgetUpdateError}
              </p>
            </div>
          )}

          <div className="flex flex-col">
            <span className={labelCls}>Último contato</span>
            <p className={`${valueCls} tabular-nums`}>
              {computer.lastSeenAt
                ? `${new Date(computer.lastSeenAt).toLocaleString("pt-BR")} (${lastSeenLabel(computer.lastSeenAt)})`
                : "—"}
            </p>
          </div>

          <FleetAgentsPanel
            equipamentId={computer.id}
            ownerEmployeeId={computer.employeeId}
            ownerPinned={computer.ownerPinned}
            onOwnerChanged={onChanged}
          />

          <FleetSessionsPanel equipamentId={computer.id} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          onClick={handleClose}
          className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
        >
          Fechar
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalComputerDetail;
